import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '@core/api/baseQuery';
import type { OrganizationSummary, PaginatedResponse, PropertySummary } from '@core/types/api';

/**
 * Accept both a bare list and a DRF pagination envelope.
 *
 * Whether a read-only collection opts out of pagination (`pagination_class =
 * None`) is a backend detail; unwrapping here means the SPA keeps working if it
 * ever changes.
 */
function unwrapList<T>(response: unknown): T[] {
  if (Array.isArray(response)) {
    return response as T[];
  }
  return (response as PaginatedResponse<T> | null)?.results ?? [];
}

/**
 * Organization API using RTK Query.
 *
 * `listOrganizations` is USER-scoped — it describes the caller's memberships (or
 * every organization, for a superuser), so it must NOT be reset when the active
 * organization changes. `listProperties` is organization-scoped: the backend
 * filters it by the `X-Organization` header the base query attaches.
 */
export const organizationApi = createApi({
  reducerPath: 'organizationApi',
  baseQuery,
  tagTypes: ['Property'],
  endpoints: (builder) => ({
    listOrganizations: builder.query<OrganizationSummary[], void>({
      query: () => ({
        url: '/api/accounts/organizations/',
        method: 'GET',
      }),
      transformResponse: (response: unknown) => unwrapList<OrganizationSummary>(response),
    }),
    listProperties: builder.query<PropertySummary[], void>({
      query: () => ({
        url: '/api/accounts/properties/',
        method: 'GET',
      }),
      transformResponse: (response: unknown) => unwrapList<PropertySummary>(response),
      providesTags: ['Property'],
    }),
  }),
});

// `listProperties` keeps its endpoint definition (it mirrors a real backend route
// and its response type is pinned by the API-contract test) but exports no hook
// until a screen calls it — `organizationApi.endpoints.listProperties` is there.
export const { useListOrganizationsQuery } = organizationApi;
